import React, {
  Fragment,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BANDS_DJS_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { formatDateFrom } from '@zola-helpers/client/dist/es/util/dateUtils';
import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { ReviewCardView } from '@zola/svc-marketplace-ts-types';
import { FavoriteHeartButton } from '@zola/zola-ui/src/components/Favorites/FavoriteHeart';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
import { TagV2Size } from '@zola/zola-ui/src/components/Tag/TagV2.styles';
import { ViewMore } from '@zola/zola-ui/src/components/ViewMore/ViewMore';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import cx from 'classnames';
import _uniq from 'lodash/uniq';

import BestOfZolaPng from '~/assets/images/badges/best_of_zola.png';
import { doTrackVendorCardClickAndView } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { getStorefrontDetails } from '~/libs/client/api/storefronts';
import { filterSupportedAwards } from '~/meta/awards';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { StorefrontDetailsProvider } from '~/pages/couples/storefronts/contexts/StorefrontDetailsProvider';
import { CouplesStorefrontDetails } from '~/types/storefrontDetails';
import { formatLocation } from '~/util/formatters';
import { StandardLogFn } from '~/util/logger';
import { formatRating } from '~/util/reviewUtils';
import getCanonicalUrl from '~/util/storefrontUrl';
import { TrackingProductLocation } from '~/util/trackingHelper';

import { StorefrontFavoriteButton } from '../../favorites/StorefrontFavoriteButton';
import CachedImage from '../../images/CachedImage';
import { useShowPostFavoriteOptIn } from '../../modals/InvitesFromVendorsOptIn/showPostFavoriteOptIn';
import { getLightboxPhotoUuids } from '../../ui/CoverGalleryV2/CoverGalleryV2';
import MobileLightbox from '../../ui/CoverGalleryV2/MobileLightbox';
import NotMobileLightbox from '../../ui/CoverGalleryV2/NotMobileLightbox';
import InquiryButton from '../../VendorQuickView/SearchCardQuickViewModal/components/InquiryButton';
import CardLinkWrapper from '../components/CardLinkWrapper';
import { VendorCardVendor } from '../util/types';
import {
  getCardDetailsMeta,
  getVendorCardPriceMetaFields,
} from '../VendorCard/components/CardDetails';
import { VendorCardPhoto, wasCardPhotoClick } from '../VendorCard/VendorCardPhoto';

import styles from './preAuthVendorCard.module.less';

const DESCRIPTION_LINE_HEIGHT = 20;

const Scrollable: React.FC<{ height: number; gradientHeight: number }> = ({
  children,
  height,
  gradientHeight,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [maskImage, setMaskImage] = useState('none');

  const setScrolled = useCallback(() => {
    if (ref.current) {
      const { scrollHeight, clientHeight, scrollTop } = ref.current;
      const isScrollable = scrollHeight > clientHeight;
      if (isScrollable) {
        const isScrolledToBottom = scrollHeight - clientHeight === scrollTop;
        const isScrolledToTop = scrollTop === 0;
        if (isScrolledToBottom) {
          setMaskImage(`linear-gradient(to bottom, transparent 0%, black ${gradientHeight}px)`);
        } else if (isScrolledToTop) {
          setMaskImage(
            `linear-gradient(to bottom, black calc(100% - ${gradientHeight}px), transparent 100%)`
          );
        } else {
          setMaskImage(
            `linear-gradient(to bottom, transparent 0%, black ${gradientHeight}px, black calc(100% - ${gradientHeight}px), transparent 100%)`
          );
        }
      } else {
        setMaskImage('none');
      }
    }
  }, [gradientHeight]);

  useEffectOnce(() => {
    setScrolled();
  });

  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('scroll', setScrolled);
    }
    return () => {
      current?.removeEventListener('scroll', setScrolled);
    };
  }, [ref, setScrolled]);

  return (
    <div className={styles.scrollableContainer} style={{ maxHeight: height, maskImage }} ref={ref}>
      {children}
    </div>
  );
};

const Rating = ({ vendor }: { vendor: VendorCardVendor }): JSX.Element => {
  const { averageReviewsRate, reviewsCount } = vendor;

  if (!averageReviewsRate) {
    return <Fragment />;
  }
  return (
    <StarRatings
      activeStars={formatRating(averageReviewsRate)}
      totalStars={5}
      fillColor={COLORS3.YELLOW_100}
      starSize="small"
      textSize="small"
      reviewCount={reviewsCount || 0}
      showReviewCount={false}
    />
  );
};

const Location = ({ vendor }: { vendor: VendorCardVendor }) => {
  // For featured vendors, only show their state/province is the searched location
  // is the same as their HOME market (metroType = HOME).  Other metro types are HOME_SERVICE
  // meaning they service the market without a fee and AWAY meaning they are willing to travel
  // but charge a fee.
  //
  // Logic check:
  //   1) if (vendor.metroType !== 'HOME' && vendor.featuredPlacement) { return <Fragment />}
  //   2) if (!(vendor.metroType !== 'HOME' && vendor.featuredPlacement) { return <span />}
  //   3) if( vendor.metroType === HOME || !vendor.featuredPlacement) { return <span> }
  if (
    vendor.city &&
    vendor.stateProvince &&
    (vendor.metroType === 'HOME' || Boolean(vendor.featuredPlacement) === false)
  ) {
    return <span data-test-id="location">{formatLocation(vendor)}</span>;
  }
  return <Fragment />;
};

const CardDetails = ({ vendor }: { vendor: VendorCardVendor }): JSX.Element => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedLocation } = vendorSearch;
  const queriedLocation = selectedLocation?.label || '';

  const cardDetailMeta = useMemo(() => {
    const { taxonomyNodeKey } = vendor;
    const { displayPrice, perPriceCopy } = getVendorCardPriceMetaFields({ vendor });

    const fullDetailList = getCardDetailsMeta({
      vendor,
      displayPrice,
      queriedLocation,
      perPriceCopy,
    });

    return fullDetailList.filter((meta) => {
      if (!meta.isCategory) return true;

      // No search category, show the category on the card
      if (!vendorSearch.selectedTaxonomy) return true;

      // Always show the category for musicians
      return taxonomyNodeKey === BANDS_DJS_TAXONOMY_KEY;
    });
  }, [queriedLocation, vendor, vendorSearch.selectedTaxonomy]);

  return (
    <ul className={styles.detailList}>
      {cardDetailMeta.map((meta, index) => {
        return (
          <li className={styles.detailItem} style={{ color: meta.color }} key={index}>
            {meta.icon({ height: 20, width: 20, showTitle: false })}
            <span data-testid={meta.dataTestId}>{meta.longLabel || meta.label}</span>
          </li>
        );
      })}
    </ul>
  );
};

const FeaturedReviewImage = ({
  review,
  vendor,
}: {
  review: ReviewCardView;
  vendor: VendorCardVendor;
}) => {
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const [storefrontDetails, setStorefrontDetails] = useState<CouplesStorefrontDetails | null>(null);

  const [photoUuids, setPhotoUuids] = useState<string[]>(review.reviewImageIds);

  const loadStorefrontDetailsAndShowLightbox = useCallback(
    (imageId: string): MouseEventHandler =>
      (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (storefrontDetails) {
          setSelectedUuid(imageId);
        } else {
          getStorefrontDetails(vendor.storefrontSlug as string)
            .then((details) => {
              const ids: string[] = [
                ...review.reviewImageIds,
                ...details?.recommendations
                  .flatMap((r) => r.reviewImageIds)
                  .filter((id): id is string => Boolean(id)),
                ...getLightboxPhotoUuids(details),
              ];

              setPhotoUuids(_uniq(ids));

              setStorefrontDetails(details);
            })
            .then(() => {
              setSelectedUuid(imageId);
            })
            .catch(StandardLogFn);
        }
      },
    [review.reviewImageIds, storefrontDetails, vendor.storefrontSlug]
  );
  const onCloseLightbox = () => {
    setSelectedUuid(null);
  };

  if (review.reviewImageIds.length === 0) {
    return <Fragment />;
  }

  return (
    <Fragment>
      {selectedUuid && storefrontDetails && (
        <StorefrontDetailsProvider storefrontDetails={storefrontDetails}>
          {isMobile() ? (
            <MobileLightbox
              photoUuids={photoUuids}
              initialUuid={selectedUuid}
              onClose={onCloseLightbox}
              reviews={storefrontDetails.recommendations}
              showFooter
            />
          ) : (
            <NotMobileLightbox
              photoUuids={photoUuids}
              initialUuid={selectedUuid}
              onClose={onCloseLightbox}
              reviews={storefrontDetails.recommendations}
            />
          )}
        </StorefrontDetailsProvider>
      )}

      <div
        className={styles.reviewImage}
        onClick={loadStorefrontDetailsAndShowLightbox(review.reviewImageIds[0])}
      >
        <CachedImage
          uuid={review.reviewImageIds[0]}
          srcSizes={[{ width: 600, height: 400 }]}
          fit="crop"
          quality={60}
          className="reviewPhoto"
        />
      </div>
    </Fragment>
  );
};

const FeaturedReview = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { featuredReview } = vendor;
  const { preAuthV2Additions } = useSearchResultsPageContext();

  const authorInfo: string = useMemo(() => {
    const parts = [];
    if (featuredReview) {
      parts.push(featuredReview.reviewerName);

      if (featuredReview.createdAt) {
        parts.push(formatDateFrom(featuredReview.createdAt, new Date()));
      }
    }
    return parts.join(', ');
  }, [featuredReview]);

  if (featuredReview) {
    return (
      <div className={styles.reviewWrapper}>
        <div className={styles.review}>
          <P.BodySmall className={styles.reviewContent} component="div">
            <ViewMore
              backgroundColor={COLORS3.BLACK_005}
              lines={2}
              viewLess={() => <Fragment />}
              viewMore={({ setIsOpen }) => (
                <LinkV2
                  sizes="smaller"
                  role="button"
                  noTextTransform
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsOpen(true);
                  }}
                >
                  Read more
                </LinkV2>
              )}
            >
              <P.BodySmall component="div" color="BLACK_075">
                &ldquo;{featuredReview.reviewText}&rdquo;
              </P.BodySmall>
            </ViewMore>
          </P.BodySmall>
          <span className={styles.authorInfo}>{authorInfo}</span>
        </div>
        {preAuthV2Additions && <FeaturedReviewImage review={featuredReview} vendor={vendor} />}
      </div>
    );
  }

  return <Fragment />;
};

const BestOfZola = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { awards } = vendor;

  const hasAward = useMemo(() => {
    return filterSupportedAwards(awards).length > 0;
  }, [awards]);

  if (!hasAward) {
    return <Fragment />;
  }

  return <img alt="Awarded Best of Zola" height={56} src={BestOfZolaPng} width={56} />;
};

const FavoriteHeart = ({ vendor, position }: { vendor: VendorCardVendor; position: number }) => {
  const { storefrontUuid } = vendor;

  const afterFavoriteCallback = useShowPostFavoriteOptIn();

  if (storefrontUuid) {
    return (
      <StorefrontFavoriteButton
        uuid={storefrontUuid}
        afterFavoriteCallback={afterFavoriteCallback}
        suppressToast
        tilePosition={position}
        location={TrackingProductLocation.SEARCH_RESULTS}
      >
        {(props) => {
          const title = props.isFavorited
            ? `Unfavorite ${vendor.vendorName}`
            : `Favorite ${vendor.vendorName}`;

          return (
            <FavoriteHeartButton
              {...props}
              className={styles.favoriteHeart}
              title={title}
              aria-label={title}
            />
          );
        }}
      </StorefrontFavoriteButton>
    );
  }
  return <Fragment />;
};

const CardImage = ({
  vendor,
  index,
  lazy,
}: {
  vendor: VendorCardVendor;
  index: number;
  lazy?: boolean;
}) => {
  const { imageIds } = vendor;
  const srcSizes =
    index === 0
      ? [
          { width: 1000, height: 666 },
          { width: 900, height: 600 },
          { width: 750, height: 500 },
          { width: 600, height: 400 },
          { width: 500, height: 333 },
          { width: 450, height: 300 },
          { width: 375, height: 250 },
          { width: 300, height: 200 },
        ]
      : [
          { width: 360, height: 240 },
          { width: 240, height: 160 },
          { width: 180, height: 120 },
          { width: 120, height: 80 },
          { width: 90, height: 60 },
        ];
  const srcSizeIndex = Math.floor(srcSizes.length / 2);
  const loading = lazy ? 'lazy' : 'eager';

  if (imageIds && imageIds.length > index) {
    return (
      <CachedImage
        uuid={imageIds[index]}
        srcSizes={srcSizes}
        width={srcSizes[srcSizeIndex].width}
        height={srcSizes[srcSizeIndex].height}
        fit="crop"
        quality={60}
        loading={loading}
        explicitSize
      />
    );
  }

  return <div />;
};

const TestPhoto = ({ vendor, lazy }: { vendor: VendorCardVendor; lazy?: boolean }) => {
  const { imageIds } = vendor;

  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const [storefrontDetails, setStorefrontDetails] = useState<CouplesStorefrontDetails | null>(null);

  const [photoUuids, setPhotoUuids] = useState<string[]>(imageIds || []);

  const loadStorefrontDetailsAndShowLightbox = useCallback(
    (imageId): MouseEventHandler =>
      (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (storefrontDetails) {
          setSelectedUuid(imageId);
        } else {
          getStorefrontDetails(vendor.storefrontSlug as string)
            .then((details) => {
              setStorefrontDetails(details);

              const ids: string[] = [
                ...getLightboxPhotoUuids(details),
                ...details?.recommendations
                  .flatMap((r) => r.reviewImageIds)
                  .filter((id): id is string => Boolean(id)),
              ];

              setPhotoUuids(_uniq(ids));
            })
            .then(() => {
              setSelectedUuid(imageId);
            })
            .catch(StandardLogFn);
        }
      },
    [storefrontDetails, vendor.storefrontSlug]
  );
  const onCloseLightbox = () => {
    setSelectedUuid(null);
  };

  const insufficientPhotos = useMemo(() => (imageIds || []).length < 4, [imageIds]);

  return (
    <Fragment>
      <div className={cx(styles.photoGrid, { [styles.insufficientPhotos]: insufficientPhotos })}>
        <div className={styles.photo} onClick={loadStorefrontDetailsAndShowLightbox(imageIds?.[0])}>
          {/* <ResponsiveVendorTagV2 size={TagV2Size.REGULAR} badges={badges} /> */}
          <CardImage vendor={vendor} index={0} lazy={lazy} />
        </div>

        {!insufficientPhotos && (
          <Fragment>
            <div
              className={styles.photo}
              onClick={loadStorefrontDetailsAndShowLightbox(imageIds?.[1])}
            >
              <CardImage vendor={vendor} index={1} lazy={lazy} />
            </div>
            <div
              className={styles.photo}
              onClick={loadStorefrontDetailsAndShowLightbox(imageIds?.[2])}
            >
              <CardImage vendor={vendor} index={2} lazy={lazy} />
            </div>
            <div
              className={styles.photo}
              onClick={loadStorefrontDetailsAndShowLightbox(imageIds?.[3])}
            >
              <CardImage vendor={vendor} index={3} lazy={lazy} />
            </div>
          </Fragment>
        )}
      </div>

      {selectedUuid && storefrontDetails && (
        <StorefrontDetailsProvider storefrontDetails={storefrontDetails}>
          {isMobile() ? (
            <MobileLightbox
              photoUuids={photoUuids}
              initialUuid={selectedUuid}
              onClose={onCloseLightbox}
              reviews={storefrontDetails.recommendations}
              showFooter
            />
          ) : (
            <NotMobileLightbox
              photoUuids={photoUuids}
              initialUuid={selectedUuid}
              reviews={storefrontDetails.recommendations}
              onClose={onCloseLightbox}
            />
          )}
        </StorefrontDetailsProvider>
      )}
    </Fragment>
  );
};

const ControlPhoto = ({ vendor }: { vendor: VendorCardVendor }) => {
  /** Click handler for the photo to prevent navigation / click tracking when the carousel is used */
  const onPhotoClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (!wasCardPhotoClick(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <VendorCardPhoto
      vendor={vendor}
      scaleOnHover={false}
      altScaleOnHover={undefined}
      showBadges={true}
      tagSize={TagV2Size.REGULAR}
      showCarousel={true}
      carouselPhotoIds={[]}
      lazyload={false}
      readOnly={false}
      scrollContainer={undefined}
      className="vendor-card-photo"
      onClick={onPhotoClick}
    />
  );
};

const ProConList = ({ items, hed }: { items: string[]; hed: string }) => {
  if (items.length > 0) {
    return (
      <div>
        <P.BodySmall className={styles.proConListHed} color={'BLACK_075'}>
          {hed}
        </P.BodySmall>
        <ul>
          {items.slice(0, 3).map((item, index) => (
            <li className={styles.proConListItem} key={index}>
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return <Fragment />;
};

const ProsAndCons = ({ vendor }: { vendor: VendorCardVendor }) => {
  const { preAuthV2Additions } = useSearchResultsPageContext();
  const { pros, cons } = vendor;

  if (!preAuthV2Additions) {
    return <Fragment />;
  }

  if ((pros || []).length > 0 || (cons || []).length > 0) {
    return (
      <div className={styles.prosAndConsWrapper}>
        <ProConList hed="Why you’ll love this venue..." items={pros || []} />
        <ProConList hed="This venue is not for you if..." items={cons || []} />
      </div>
    );
  }
  return <Fragment />;
};

export const PreAuthVendorCard = ({
  vendor,
  position,
  loading,
  className,
  ...rest
}: {
  vendor: VendorCardVendor;
  position: number;
  loading: boolean;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, 'onClick'>): JSX.Element => {
  const { storefrontSlug, taxonomyNodeKey } = vendor;

  const { isPreAuthSrpTest } = useSearchResultsPageContext();

  const listingPagePath = useMemo(() => {
    return storefrontSlug && taxonomyNodeKey
      ? getCanonicalUrl(taxonomyNodeKey, storefrontSlug)
      : null;
  }, [storefrontSlug, taxonomyNodeKey]);

  /** Click handler for the card: just tracking, let the default behavior navigate */
  const onCardClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // This is a great mystery.  Without this, returning to the SRP and hovering
      // a _different card_ causes this card to enter a hover/active state and show
      // text underlines, evn through we've disabled text-underlines on hover.
      e.currentTarget.blur();

      doTrackVendorCardClickAndView({
        vendor,
        position,
        listingShelfName: 'SEARCH_RESULTS_PAGE',
        includeTags: true,
      });
    },
    [position, vendor]
  );

  const nameParts = (vendor.vendorName || '').split(' ');

  return (
    <CardLinkWrapper
      className={cx(styles.vendorCard, className, { [styles.blur]: loading })}
      to={listingPagePath}
      onClick={onCardClick}
      {...rest}
    >
      {isPreAuthSrpTest ? (
        <TestPhoto vendor={vendor} lazy={position > 1} />
      ) : (
        <ControlPhoto vendor={vendor} />
      )}

      <div className={styles.body} data-testid="search-result">
        <div className={styles.topRow}>
          <div className={styles.left}>
            <span className={styles.hed}>
              <span className={styles.vendorName} data-testid="vendor-name">
                {nameParts.slice(0, -1).join(' ')}
                <span className={styles.noWrap}>
                  {nameParts.length > 1 && <>&nbsp;</>}
                  {nameParts.slice(-1)}&nbsp;
                  <FavoriteHeart vendor={vendor} position={position} />
                </span>
              </span>
            </span>
            <div className={styles.ratingAndLocation}>
              <Rating vendor={vendor} />
              <Location vendor={vendor} />
            </div>
          </div>
          {isPreAuthSrpTest && vendor.claimed && (
            <div className={styles.right}>
              <InquiryButton
                trackingLocation={TrackingProductLocation.SEARCH_RESULTS}
                storefrontUuid={vendor.storefrontUuid as string}
                slug={vendor.storefrontSlug}
              />
            </div>
          )}
        </div>
        <CardDetails vendor={vendor} />
        <div className={styles.vendorDetails}>
          <Scrollable height={4 * DESCRIPTION_LINE_HEIGHT} gradientHeight={DESCRIPTION_LINE_HEIGHT}>
            <P.BodySmall className={styles.description} color="BLACK_075">
              {vendor.description}
            </P.BodySmall>
            <ProsAndCons vendor={vendor} />
          </Scrollable>
          <BestOfZola vendor={vendor} />
        </div>
        <FeaturedReview vendor={vendor} />
        {isPreAuthSrpTest && vendor.claimed && (
          <div className={styles.bottom}>
            <InquiryButton
              trackingLocation={TrackingProductLocation.SEARCH_RESULTS}
              storefrontUuid={vendor.storefrontUuid as string}
              slug={vendor.storefrontSlug}
            />
          </div>
        )}
      </div>
    </CardLinkWrapper>
  );
};
